<template>
	<div class="question-page">
		<div
			class="section-header"
			:style="{ backgroundColor: theme.mainColor, color: theme.altTextColor }"
		>
			<div
				class="header-fullname"
				:style="{
					color: theme.secondaryColor,
					borderColor: theme.lightMainTextColor,
				}"
			>
				{{ util.getProperCasePhrase(fullName) }}
			</div>
			<div class="category-container">
				<div
					class="categories"
					v-for="(c, index) in categories"
					:key="c.key"
					:class="{ active: categoryActive(c) }"
					:style="categoryTextStyle(c, index)"
				>
					{{ categorySeen(c) ? c.label[customerAppBasic.language] : '\u00A0' }}
					<span :style="categoryBulletStyle(c, index)"></span>
				</div>
			</div>
			<div
				v-if="messages.length"
				class="messages-container"
			>
				<p
					v-for="(message, index) in messages"
					:key="index"
					class="messages"
				>
					{{ message }}
				</p>
			</div>
		</div>
		<main :style="{ backgroundColor: 'white', color: theme.mainTextColor }">
			<div
				class="main-section"
				ref="questionComponent"
			>
				<div class="question-header">
					<div
						class="back-button"
						:style="{
							color: theme.mainTextColor,
							borderColor: theme.lightMainTextColor,
						}"
						v-if="shouldBackButtonBeEnabled() && actualQuestion.prevQuestion"
						@click.prevent="handleBackButton"
					>
						<i class="fas fa-angle-left"></i>
						<button class="link back">
							{{ localization.buttonLabels['back'][customerAppBasic.language] }}
						</button>
					</div>
					<div v-if="!shouldBackButtonBeEnabled() || !actualQuestion.prevQuestion"></div>
					<button
						class="language-change back-button"
						@click="languageChange"
					>
						{{ localizationOposite }} <i class="fas fa-globe"></i>
					</button>
				</div>
				<div
					class="loading"
					v-if="isBusy"
				>
					<LoaderComponent />
				</div>
				<component
					v-else
					:is="actualQuestion.type"
					v-bind="{
						theme: customerAppBasic.storeInfo.theme,
						data: {
							question: actualQuestion,
							language: customerAppBasic.language,
							isSavingDeal: isSavingDeal,
							application: applicationObject,
							sessionStatus: {
								isNew: isNewSession,
								update: updateSessionStatus,
							},
						},
						questionKey: actualQuestion.key,
						toggleLoading,
						saveApplicationAction,
					}"
				></component>
			</div>
			<div class="bottom-section"></div>
		</main>

		<!--<footer :style="{backgroundColor: theme.secondaryColor, color: theme.secondaryTextColor}">
            <div>Powered by <span>Saturn</span></div>
        </footer>-->
	</div>
</template>

<script>
	//import CreditAppData from "@core/classes/CreditAppClasses/CreditAppData"
	import Application from '@core/classes/Applications/Application';
	import * as applicationSettingsAPIs from '@core/classes/Applications/ApplicationsSettingsAPIs';
	import QuestionAutoComplete from '@/components/customerApp/QuestionAutoComplete.vue';
	import QuestionDate from '@/components/customerApp/QuestionDate.vue';
	import QuestionFiller from '@/components/customerApp/QuestionFiller.vue';
	import QuestionInputCurrency from '@/components/customerApp/QuestionInputCurrency.vue';
	import QuestionInputDropdown from '@/components/customerApp/QuestionInputDropdown.vue';
	import QuestionInputMultiAnswer from '@/components/customerApp/QuestionInputMultiAnswer.vue';
	import QuestionInputNumber from '@/components/customerApp/QuestionInputNumber.vue';
	import QuestionInputPhone from '@/components/customerApp/QuestionInputPhone.vue';
	import QuestionInputText from '@/components/customerApp/QuestionInputText.vue';
	import QuestionMultiButtons from '@/components/customerApp/QuestionMultiButtons.vue';
	import QuestionMultiSelect from '@/components/customerApp/QuestionMultiSelect.vue';
	import QuestionPlaidLink from '@/components/customerApp/QuestionPlaidLink.vue';
	import QuestionSocial from '@/components/customerApp/QuestionSocial.vue';
	import QuestionSummary from '@/components/customerApp/QuestionSummary.vue';
	import QuestionTextMessage from '@/components/customerApp/QuestionTextMessage.vue';
	import QuestionYesNo from '@/components/customerApp/QuestionYesNo.vue';
	import util from '@core/services/util';
	import LOCALIZATION from '@core/classes/Localization';
	import api from '@core/services/api/_api';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import LogRocket from 'logrocket';
	import ENUMS from '@core/classes/Enums';
	import DocumentAssociation from '@core/classes/DocumentAssociation';
	import { EventBusCore } from '@core/services/util';

	export default {
		name: 'CreditAppQuestionnaire',
		props: ['application', 'customerAppBasic', 'terms', 'acceptedTermsDate', 'acceptedTermsDateOffset'],
		data() {
			return {
				isNewSession: true,
				isBusy: false,
				isSavingDeal: null,
				applicationObject: null,
				actualQuestion: {},
				emptyQuestionObject: {
					key: 'emptyQuestion',
					type: 'QuestionFiller',
					category: '',
				},
				categories: Object.keys(LOCALIZATION.categories)
					.filter(c => c != 'thankYou')
					.map(c => ({
						key: c,
						label: LOCALIZATION.categories[c],
					})),
				theme: {},
				loginError: false,
				isLoading: false,
				pageInitialHeight: 0,
				messages: [],
			};
		},

		computed: {
			localization() {
				return LOCALIZATION;
			},
			localizationOposite() {
				if (this.customerAppBasic.language == 'Spanish') {
					return 'English';
				} else {
					return 'Español';
				}
			},
			util() {
				return util;
			},
			fullName() {
				if (this.application?.preFilledData?.firstName && this.application?.preFilledData?.lastName) {
					return this.applicationObject?.quoteAppData?.applicationsDataAnswered?.personalInfo?.firstName &&
						this.applicationObject?.quoteAppData?.applicationsDataAnswered?.personalInfo?.lastName
						? this.applicationObject?.quoteAppData?.applicationsDataAnswered?.personalInfo?.firstName +
								' ' +
								this.applicationObject?.quoteAppData?.applicationsDataAnswered?.personalInfo?.lastName
						: this.application?.preFilledData?.firstName + ' ' + this.application?.preFilledData?.lastName;
				}
				return '';
			},
		},
		beforeUnmount() {
			EventBusCore.off('goToPreviousQuestion', this.handlePreviousQuestion);
			EventBusCore.off('goToNextQuestion', this.handleNextQuestion);
		},
		async created() {
			this.applicationObject = new Application(this.application);
			this.applicationObject.quoteAppData.language = this.customerAppBasic.language;
			if (this.applicationObject.plaidId) {
				const documentAssociation = new DocumentAssociation({
					type: ENUMS.DOC_ASSOCIATION.CustomerApplication,
					id: this.applicationObject.id,
				});
				const response = await api.plaid.getPastPlaidExchanges([documentAssociation]);
				if (response && response.data && response.data.length >= 1) {
					applicationSettingsAPIs.plaidInfo(response.data[response.data.length - 1].identityVerification.response, null, null, this.applicationObject);
				}
			}

			this.actualQuestion = this.getCurrentQuestion();
			this.theme = this.customerAppBasic.storeInfo.theme;

			window.addEventListener('beforeunload', this.handleBeforeClosePage);

			EventBusCore.on('goToPreviousQuestion', this.handlePreviousQuestion);
			EventBusCore.on('goToNextQuestion', this.handleNextQuestion);

			//if (this.applicationObject.dateCompleted) {
			//    this.messages.push(this.localization.miscellaneous.statusMessage[this.customerAppBasic.language]);
			//}
		},
		methods: {
			handleBackButton() {
				this.actualQuestion.getPreviousQuestion();
			},
			updateSessionStatus() {
				this.isNewSession = false;
			},
			handlePreviousQuestion() {
				this.prepareForNextQuestion(true);
			},
			handleNextQuestion() {
				this.prepareForNextQuestion();
			},
			languageChange() {
				if (this.customerAppBasic.language == 'English') {
					this.customerAppBasic.language = 'Spanish';
				} else {
					this.customerAppBasic.language = 'English';
				}
			},
			shouldBackButtonBeEnabled() {
				const currentQuestionObj = this.applicationObject.quoteAppData.questions.find(q => q.key === this.applicationObject.quoteAppData.currentQuestion);

				if (this.applicationObject.quoteAppData.pathFollowed.length == 0 || this.applicationObject.isSingleQuestionEdit || currentQuestionObj.isFirstInGroup) {
					return false;
				}

				if (!this.applicationObject.isCompleted()) {
					return true;
				}

				const previousQuestionKey = this.applicationObject.quoteAppData.pathFollowed[this.applicationObject.quoteAppData.pathFollowed.length - 1];
				const previousQuestionCategory = this.applicationObject.quoteAppData.questions.find(q => q.key === previousQuestionKey)?.category;
				return this.actualQuestion.category === previousQuestionCategory;
			},
			getCurrentQuestion() {
				//console.log(this.creditApp)
				this.saveDataInLocalStorage();
				return this.applicationObject.quoteAppData.questions.find(q => q.key == this.applicationObject.quoteAppData.currentQuestion);
			},
			saveDataInLocalStorage() {
				localStorage.setItem('fimenuObject', JSON.stringify(this.customerAppBasic));
				localStorage.setItem('applicationObject', util.encryptJSONText(JSON.stringify(this.applicationObject)));
			},
			prepareForNextQuestion(goingBackwards = false) {
				this.$refs.questionComponent.classList.add('hide');
				let nextQuestion = this.getCurrentQuestion();

				// We're hiding the component after the animation is gone. Which is in 300ms
				setTimeout(() => {
					/*This component is called in between real question components to avoid reactivity problems when the same component is rendered twice in a row*/
					this.emptyQuestionObject.category = nextQuestion.category;
					this.actualQuestion = this.emptyQuestionObject;
				}, 300);

				this.setupNextQuestion(nextQuestion);
			},
			async saveApplicationAction() {
				const nextQuestion = this.getCurrentQuestion();

				this.isSavingDeal = true;

				const acceptedTerms = this.terms.map(t => ({
					id: t.id,
					termsType: t.termsType,
					termsLabel: t.termsLabel,
					termsText: t.termsText,
				}));

				// Determine if the path needs to be updated
				let shouldKeepOriginalPath = this.application?.dateCompleted != null;
				const fullPathFollowed = this.application.quoteAppData?.fullPathFollowed;

				// Old applications will not have a leadSource so we set it for backwards compatibility
				if (this.$route.query.leadSource && !this.applicationObject.createAppParams['leadSource']) {
					this.applicationObject.createAppParams['leadSource'] = this.$route.query.leadSource;
				}

				// Revert path to original if needed
				if (shouldKeepOriginalPath) {
					this.applicationObject.quoteAppData.pathFollowed = fullPathFollowed;
				}

				try {
					const response = await api.applications.saveApplication({
						application: this.applicationObject,
						isFinished: nextQuestion.isEndOfApp ?? false,
						isNewSession: this.isNewSession,
						isNextQuestionACompletionPage: !!nextQuestion.isCompleted,
						userDateTime: this.acceptedTermsDate,
						userDateTimeOffset: this.acceptedTermsDateOffset,
						acceptedTerms,
					});

					if (response.data.payload.success) {
						this.isSavingDeal = false;
						LogRocket.log('Customer saved response:', response.data.payload.customer);
						response.data.payload.application.quoteAppData.fullPathFollowed = fullPathFollowed;

						//Update only the property of the applicationObject that are returned by the saveApplication response.
						this.applicationObject.update(response.data.payload.application);
					}
				} finally {
					this.updateSessionStatus();
				}
			},
			setupNextQuestion(nextQuestion) {
				setTimeout(() => {
					nextQuestion.populateSavedAnswer();
					this.actualQuestion = nextQuestion;
					this.$refs.questionComponent.classList.remove('hide');
				}, 350);
			},
			categoryActive(c) {
				return c.key == this.actualQuestion.category;
			},
			categorySeen(c) {
				const actualIndex = this.categories.findIndex(cat => cat.key == this.actualQuestion.category);
				const categoryIndex = this.categories.findIndex(cat => cat.key == c.key);

				return categoryIndex <= actualIndex;
			},
			categoryBulletStyle(c, index) {
				const actual = this.categories.findIndex(ca => ca.key == this.actualQuestion.category);

				if (c.key == this.actualQuestion.category) {
					// active category
					return {
						borderColor: this.theme.categoryActiveBulletBorderColor,
						background: this.theme.categoryActiveBulletBackground,
					};
				} else if (actual > index) {
					// past categories
					return {
						borderColor: this.theme.categorySuccessBulletBorderColor,
						background: this.theme.categorySuccessBulletBackground,
					};
				} else {
					// not reached categories
					return {
						borderColor: this.theme.categoryBulletBorderColor,
						background: this.theme.categoryBulletBackground,
					};
				}
			},
			categoryTextStyle(c, index) {
				const actual = this.categories.findIndex(ca => ca.key == c);

				if (c.key == this.actualQuestion.category) {
					// active category
					return {
						color: this.theme.categoryActiveText,
					};
				} else if (actual > index) {
					// past categories
					return {
						color: this.theme.categorySuccessText,
					};
				} else {
					// not reached categories
					return {
						color: this.theme.categoryText,
					};
				}
			},
			onResizeHandler() {
				const metaViewport = document.querySelector('meta[name=viewport]');

				if (window.innerHeight < this.pageInitialHeight) {
					metaViewport.setAttribute('content', 'height=' + this.pageInitialHeight + 'px,width=device-width,initial-scale=1.0');
				} else {
					metaViewport.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=0');
				}
			},
			toggleLoading() {
				this.isBusy = !this.isBusy;
			},
			handleBeforeClosePage(e) {
				// console.log('HANDLE BEFORE CLOSE PAGE');
				if (!this.applicationObject.quoteAppData.questions.find(q => q.key == this.applicationObject.quoteAppData.currentQuestion).isEndOfApp) {
					e.preventDefault();
					e.returnValue = '1';
				}
			},
		},
		components: {
			QuestionDate,
			QuestionFiller,
			QuestionInputCurrency,
			QuestionInputPhone,
			QuestionInputText,
			QuestionMultiButtons,
			QuestionSummary,
			QuestionTextMessage,
			QuestionYesNo,
			QuestionInputMultiAnswer,
			QuestionSocial,
			QuestionMultiSelect,
			QuestionInputDropdown,
			QuestionInputNumber,
			LoaderComponent,
			QuestionAutoComplete,
			QuestionPlaidLink,
		},
	};
</script>
<style></style>
